import { Division, ReportColumn, ReportDefinition } from './report.model';

/** Shorthand column builders — keeps the definitions below readable. */
const num = (field: string, header: string): ReportColumn => ({ field, header, type: 'number' });
const txt = (field: string, header: string): ReportColumn => ({ field, header, type: 'text' });
const dte = (field: string, header: string): ReportColumn => ({ field, header, type: 'date' });
// A calendar year — rendered as a plain integer (2026), not a formatted number (2,026.00).
const yr = (field: string, header: string): ReportColumn => ({ field, header, type: 'year' });

/**
 * Actuary division reports.
 *
 * Column `field` values are the lowercased JSON keys; the API serialises with
 * System.Text.Json camelCase, and ReportDataService lowercases every key on
 * load so binding is casing-proof.
 */
export const ACTUARY_REPORTS: ReportDefinition[] = [
  {
    key: 'summary',
    division: 'actuary',
    title: 'Summary Report',
    description:
      'Commercial vs private hedging positions across LRP / LGM / DRP, with totals per measure, for the selected reinsurance year.',
    requiresYear: true,
    dataMethod: 'POST',
    dataPath: 'SummaryReport/GetLiveStockSummaryReport',
    downloadPath: 'SummaryReport/DownloadSummaryReport',
    downloadFileBase: 'Livestock_Summary_Report',
    columns: [
      txt('measure', 'Measure'),
      txt('qtrstatus', 'Qtr Status'),
      num('c_lrp', 'C_LRP'),
      num('c_lgm', 'C_LGM'),
      num('c_drp', 'C_DRP'),
      num('totalhedgedcommercial', 'Total Hedged Commercial'),
      num('p_lrp', 'P_LRP'),
      num('p_lgm', 'P_LGM'),
      num('p_drp', 'P_DRP'),
      num('totalprivatemarket', 'Total Private Market'),
      num('total_lrp', 'Total_LRP'),
      num('total_lgm', 'Total_LGM'),
      num('total_drp', 'Total_DRP'),
      num('total', 'Total')
    ]
  },
  {
    key: 'expected-by-fund',
    division: 'actuary',
    title: 'Expected By Fund',
    description:
      'Gross → quota-share ceded → net-of-hedge projections grouped by fund, including XOL and A&O override.',
    requiresYear: false,
    dataMethod: 'POST',
    dataPath: 'ExpectedByFundReport/GetLiveStockExpectedByFundReport',
    downloadPath: 'ExpectedByFundReport/DownloadExpectedByFundReport',
    downloadFileBase: 'Expected_By_Fund',
    columns: [
      txt('fund', 'Fund'),
      yr('reinsuranceyear', 'Reinsurance Year'),
      num('grosspremium', 'Gross Premium'),
      num('grosspaidloss', 'Gross Paid Loss'),
      num('grossavgsimloss', 'Gross Avg Sim Loss'),
      num('grossultimateloss', 'Gross Ultimate Loss'),
      num('grossultimatelossratio', 'Gross Ult. Loss Ratio'),
      num('qscededpremium', 'QS Ceded Premium'),
      num('qscededpaidloss', 'QS Ceded Paid Loss'),
      num('qscededavgsimloss', 'QS Ceded Avg Sim Loss'),
      num('qscededultimateloss', 'QS Ceded Ultimate Loss'),
      num('qscededultimatelossratio', 'QS Ceded Ult. Loss Ratio'),
      num('netexclhedgepremium', 'Net excl. Hedge Premium'),
      num('netexclhedgepaidloss', 'Net excl. Hedge Paid Loss'),
      num('netexclhedgeavgsimloss', 'Net excl. Hedge Avg Sim Loss'),
      num('netexclhedgeultimateloss', 'Net excl. Hedge Ultimate Loss'),
      num('netexclhedgeafrcost', 'Net excl. Hedge AFR Cost'),
      num('netexclhedgeuwgain', 'Net excl. Hedge UW Gain'),
      num('netexclhedgeultimatelossratio', 'Net excl. Hedge Ult. Loss Ratio'),
      num('xolcededxolcost', 'XOL Ceded — XOL Cost'),
      num('xolcededxolbenefit', 'XOL Ceded — XOL Benefit'),
      num('xolcededxolimpact', 'XOL Ceded — XOL Impact'),
      num('aandoexpenseoverrideexpense', 'A&O Override Expense'),
      num('aandoexpenseoverrideexpenseratio', 'A&O Override Expense Ratio'),
      num('aandoexpenseoverrideuwprofit', 'A&O Override UW Profit'),
      num('aandoexpenseoverridecombinedratio', 'A&O Override Combined Ratio'),
      num('netinclhedgehedgecost', 'Net incl. Hedge — Hedge Cost'),
      num('netinclhedgehedgerecovery', 'Net incl. Hedge — Hedge Recovery'),
      num('netinclhedgehedgegainorloss', 'Net incl. Hedge — Hedge Gain/Loss'),
      num('netinclhedgeuwprofitorloss', 'Net incl. Hedge — UW Profit/Loss'),
      num('netinclhedgecombinedratio', 'Net incl. Hedge — Combined Ratio')
    ]
  },
  {
    key: 'expected-oc-qce',
    division: 'actuary',
    title: 'Expected OC / QCE',
    description:
      'Composite ratios including hedge and XOL, NPV adjustment, risk load and booked underwriting profit by QCE.',
    requiresYear: false,
    dataMethod: 'POST',
    dataPath: 'ExpectedOCQCEReport/GetLiveStockExpectedOCQCEReport',
    downloadPath: 'ExpectedOCQCEReport/DownloadExpectedOCQCEReport',
    downloadFileBase: 'Expected_OC_QCE_Report',
    columns: [
      txt('qce', 'QCE'),
      yr('cropyear', 'Crop Year'),
      num('grosspremium', 'Gross Premium'),
      num('netpremium', 'Net Premium'),
      num('paidloss', 'Paid Loss'),
      num('avgsimloss', 'Avg Sim Loss'),
      num('ultimateloss', 'Ultimate Loss'),
      num('ultimatelossratio', 'Ultimate Loss Ratio'),
      num('expense', 'Expense'),
      num('expenseratio', 'Expense Ratio'),
      num('compositeratioexclhedge', 'Composite Ratio excl. Hedge'),
      num('hedgegainloss', 'Hedge Gain/Loss'),
      num('hedgeimpact', 'Hedge Impact'),
      num('compositeratioinclhedge', 'Composite Ratio incl. Hedge'),
      num('xolcost', 'XOL Cost'),
      num('xolbenefit', 'XOL Benefit'),
      num('xolimpact', 'XOL Impact'),
      num('compositeratioinclxol', 'Composite Ratio incl. XOL'),
      num('expecteduwprofitloss', 'Expected UW Profit/Loss'),
      num('npvadjustment', 'NPV Adjustment'),
      num('riskload', 'Risk Load'),
      num('bookeduwprofitloss', 'Booked UW Profit/Loss'),
      num('uwprofitpercentage', 'UW Profit %'),
      num('uwgainpercentage', 'UW Gain %'),
      txt('reportingquarter', 'Reporting Quarter')
    ]
  },
  {
    key: 'expected-oc-range-estimates',
    division: 'actuary',
    title: 'Expected OC — Range Estimates',
    description:
      'Percentile range of underwriting profit — commercial and private — versus the previous quarter.',
    requiresYear: false,
    dataMethod: 'POST',
    dataPath: 'ExpectedOCQCEReport/GetLiveStockExpectedOCRangeEstimatesReport',
    // NB: the deployed route literally contains spaces — must stay URL-encoded.
    downloadPath: 'ExpectedOCQCEReport/DownloadExpectedOCQCEReport%20With%20RangeEstimates',
    downloadFileBase: 'Expected_OC_QCE_Report_With_RangeEstimates',
    columns: [
      yr('reinsuranceyear', 'Reinsurance Year'),
      txt('percentile', 'Percentile'),
      num('c_nwp', 'C_NWP'),
      num('c_netindem', 'C_Net Indem'),
      num('c_netlr', 'C_Net LR'),
      num('xolbenefit', 'XOL Benefit'),
      num('c_hedgebenefit', 'C_Hedge Benefit'),
      num('c_uwprofit', 'C_UW Profit'),
      num('p_nwp', 'P_NWP'),
      num('p_netindem', 'P_Net Indem'),
      num('p_netlr', 'P_Net LR'),
      num('p_hedgebenefit', 'P_Hedge Benefit'),
      num('p_uwprofit', 'P_UW Profit'),
      num('total_uwprofit', 'Total UW Profit'),
      num('previousqtruwprofit', 'Previous Qtr UW Profit'),
      num('changefrompreviousqtr', 'Change from Previous Qtr'),
      num('uwprofitpercentage', 'UW Profit %')
    ]
  }
];

/**
 * Finance division reports.
 *
 * Routes/verbs/requiresYear come straight from the API controllers
 * (AgriFinOps.Bordereau.Api) and columns from the Domain entities
 * (LivestockBordereauFinance*). As with Actuary, `field` values are the fully
 * lowercased JSON keys (ReportDataService lowercases every response key).
 */
export const FINANCE_REPORTS: ReportDefinition[] = [
  {
    key: 'finance-summary',
    division: 'finance',
    title: 'Finance Summary Report',
    description:
      'Commercial vs private hedging positions across LRP / LGM / DRP, with totals per measure, for the selected reinsurance year.',
    requiresYear: true,
    dataMethod: 'POST',
    dataPath: 'SummaryReport/GetLiveStockFinanceSummaryReport',
    downloadPath: 'SummaryReport/DownloadFinanceSummaryReport',
    downloadFileBase: 'Livestock_Finance_Summary_Report',
    columns: [
      num('seq', 'Seq'),
      txt('measure', 'Measure'),
      yr('reinsuranceyear', 'Reinsurance Year'),
      txt('qtrstatus', 'Qtr Status'),
      num('c_lrp', 'C_LRP'),
      num('c_lgm', 'C_LGM'),
      num('c_drp', 'C_DRP'),
      num('totalhedgedcommercial', 'Total Hedged Commercial'),
      num('p_lrp', 'P_LRP'),
      num('p_lgm', 'P_LGM'),
      num('p_drp', 'P_DRP'),
      num('totalprivatemarket', 'Total Private Market'),
      num('total_lrp', 'Total_LRP'),
      num('total_lgm', 'Total_LGM'),
      num('total_drp', 'Total_DRP'),
      num('total', 'Total'),
      txt('rptmeasurename', 'Rpt Measure Name'),
      dte('qtrrundate', 'Qtr Run Date')
    ]
  },
  {
    key: 'balance-sheet',
    division: 'finance',
    title: 'Balance Sheet Report',
    description:
      'Balance sheet values by transaction type, account and plan for the selected reinsurance year.',
    requiresYear: true,
    dataMethod: 'POST',
    dataPath: 'BalanceSheetReport/GetBalanceSheetReport',
    downloadPath: 'BalanceSheetReport/DownloadBalanceSheetReport',
    downloadFileBase: 'Balance_Sheet_Report',
    columns: [
      num('seq', 'Seq'),
      txt('transtype', 'Trans Type'),
      txt('account', 'Account'),
      txt('plan', 'Plan'),
      yr('reinsuranceyear', 'Reinsurance Year'),
      num('value', 'Value'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'balance-sheet-collected-paid',
    division: 'finance',
    title: 'Balance Sheet Collected Paid Report',
    description:
      'Collected/paid trial balances before and after reclassification, with adjustment entries by investment account.',
    requiresYear: false,
    dataMethod: 'POST',
    dataPath: 'BalanceSheetReport/GetBalanceSheetCollectedPaidReport',
    downloadPath: 'BalanceSheetReport/DownloadBalanceSheetCollectedPaidReport',
    downloadFileBase: 'Balance_Sheet_Collected_Paid_Report',
    columns: [
      num('seq', 'Seq'),
      txt('transtype', 'Trans Type'),
      txt('investmentaccountname', 'Investment Account Name'),
      txt('policylossposition', 'Policy Loss Position'),
      num('percentageperabove', 'Percentage Per Above'),
      num('onexpired', 'On Expired'),
      num('trialbalancepriortoreclass', 'Trial Balance Prior to Reclass'),
      num('balanceafterreclass', 'Balance After Reclass'),
      num('adjmtentry', 'Adjustment Entry'),
      num('reclassadjmt', 'Reclass Adjustment'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'balance-sheet-final-adjustment',
    division: 'finance',
    title: 'Balance Sheet Final Adjustment Report',
    description:
      'Final adjustments against SAP balances by investment account, with resulting differences after adjustment.',
    requiresYear: false,
    dataMethod: 'POST',
    dataPath: 'BalanceSheetReport/GetBalanceSheetFinalAdjustmentReport',
    downloadPath: 'BalanceSheetReport/DownloadBalanceSheetFinalAdjustmentReport',
    downloadFileBase: 'Balance_Sheet_Final_Adjustment_Report',
    columns: [
      num('seq', 'Seq'),
      txt('transtype', 'Trans Type'),
      txt('investmentaccountname', 'Investment Account Name'),
      txt('policylossposition', 'Policy Loss Position'),
      num('finaladjustment', 'Final Adjustment'),
      num('balance', 'Balance'),
      num('otherdpecadjustments', 'Other DPEC Adjustments'),
      num('afteradjustment', 'After Adjustment'),
      num('sapbalancecos', 'SAP Balance Cos'),
      num('difference', 'Difference'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'collected-paid-income-statement',
    division: 'finance',
    title: 'Collected Paid Income Statement Report',
    description:
      'Collected/paid income statement values by account and plan for the selected reinsurance year.',
    requiresYear: true,
    dataMethod: 'POST',
    dataPath: 'CollectedPaidReports/GetCollectedPaidIncomeStatementReport',
    downloadPath: 'CollectedPaidReports/DownloadCollectedPaidIncomeStatementReport',
    downloadFileBase: 'Collected_Paid_Income_Statement_Report',
    columns: [
      num('seq', 'Seq'),
      txt('account', 'Account'),
      txt('plan', 'Plan'),
      yr('reinsuranceyear', 'Reinsurance Year'),
      num('value', 'Value'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'collected-paid-balance-sheet',
    division: 'finance',
    title: 'Collected Paid Balance Sheet Report',
    description:
      'Collected/paid balance sheet values by transaction type, account and plan for the selected reinsurance year.',
    requiresYear: true,
    dataMethod: 'POST',
    dataPath: 'CollectedPaidReports/GetCollectedPaidBalanceSheetReport',
    downloadPath: 'CollectedPaidReports/DownloadCollectedPaidBalanceSheetReport',
    downloadFileBase: 'Collected_Paid_Balance_Sheet_Report',
    columns: [
      num('seq', 'Seq'),
      txt('transtype', 'Trans Type'),
      txt('account', 'Account'),
      txt('plan', 'Plan'),
      yr('reinsuranceyear', 'Reinsurance Year'),
      num('value', 'Value'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'trial-balance',
    division: 'finance',
    title: 'Trial Balance Report',
    description:
      'General-ledger trial balance by account and profit center, with prior-period and accumulated balances.',
    requiresYear: false,
    dataMethod: 'GET',
    dataPath: 'TrialBalanceReport/GetTrialBalanceReport',
    downloadPath: 'TrialBalanceReport/DownloadTrialBalanceReport',
    downloadFileBase: 'Livestock_TrialBalance_Report',
    columns: [
      txt('acctnum', 'Acct Num'),
      txt('accountnumber', 'Account Number'),
      txt('accountname', 'Account Name'),
      txt('buscatorreintypecode', 'Bus Cat / Rein Type Code'),
      txt('buscatorreintype', 'Bus Cat / Rein Type'),
      txt('fsitem', 'FS Item'),
      txt('profitcentercode', 'Profit Center Code'),
      txt('profitcenter1', 'Profit Center'),
      num('accumbal_prevperiod', 'Accum Bal Prev Period'),
      num('netchange4to6', 'Net Change 4 to 6'),
      num('accumulatedbalance', 'Accumulated Balance'),
      txt('currency', 'Currency'),
      txt('ledger', 'Ledger'),
      txt('companycode', 'Company Code'),
      dte('asofdate', 'As of Date')
    ]
  },
  {
    key: 'journal-entry',
    division: 'finance',
    title: 'Journal Entry Report',
    description:
      'Journal entry values by account and plan (LRP / LGM / DRP / All) for the selected reinsurance year.',
    requiresYear: true,
    dataMethod: 'POST',
    dataPath: 'JournalEntryReport/GetJournalEntryReport',
    downloadPath: 'JournalEntryReport/DownloadJournalEntryReport',
    downloadFileBase: 'Journal_Entry_Report',
    columns: [
      num('seq', 'Seq'),
      txt('account', 'Account'),
      txt('plan', 'Plan'),
      yr('reinsuranceyear', 'Reinsurance Year'),
      num('value', 'Value'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'investment-entry',
    division: 'finance',
    title: 'Investment Entry Report',
    description:
      'Investment entry values by account and gain/loss position for the selected reinsurance year.',
    requiresYear: true,
    dataMethod: 'POST',
    dataPath: 'InvestmentEntryReport/GetInvestmentEntryReport',
    downloadPath: 'InvestmentEntryReport/DownloadInvestmentEntryReport',
    downloadFileBase: 'Investment_Entry_Report',
    columns: [
      num('seq', 'Seq'),
      txt('account', 'Account'),
      txt('plan', 'Plan'),
      yr('reinsuranceyear', 'Reinsurance Year'),
      num('value', 'Value'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'collected-balance-reclassification',
    division: 'finance',
    title: 'Collected Balance Reclassification Report',
    description:
      'Debit/credit reclassification of collected balances by account and plan for the selected reinsurance year.',
    requiresYear: true,
    dataMethod: 'POST',
    dataPath: 'CollectedBalanceReclassificationReport/GetCollectedBalanceReclassificationReport',
    downloadPath: 'CollectedBalanceReclassificationReport/DownloadCollectedBalanceReclassificationReport',
    downloadFileBase: 'Collected_Balance_Reclassification_Report',
    columns: [
      num('seq', 'Seq'),
      txt('transactiontype', 'Transaction Type'),
      txt('account', 'Account'),
      txt('plan', 'Plan'),
      yr('reinsuranceyear', 'Reinsurance Year'),
      num('value', 'Value'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'qtrly-top-side-entry',
    division: 'finance',
    title: 'Qtrly Top Side Entry Report',
    description: 'Quarterly top-side entry values by reinsurance year.',
    requiresYear: false,
    dataMethod: 'GET',
    dataPath: 'QtrlyTopSideEntryReport/GetQtrlyTopSideEntryReport',
    downloadPath: 'QtrlyTopSideEntryReport/DownloadQtrlyTopSideEntryReport',
    downloadFileBase: 'Livestock_QtrlyTopSideEntry_Report',
    columns: [
      num('seq', 'Seq'),
      // ReinsuranceYear is a string on this entity (unlike other reports).
      txt('reinsuranceyear', 'Reinsurance Year'),
      num('value', 'Value'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'qtrly-expected-entries-itd',
    division: 'finance',
    title: 'Qtrly Expected Entries ITD Report',
    description: 'Quarterly expected inception-to-date entries by reinsurance year.',
    requiresYear: false,
    dataMethod: 'GET',
    dataPath: 'QtrlyExpectedEntriesITDReport/GetQtrlyExpectedEntriesITDReport',
    downloadPath: 'QtrlyExpectedEntriesITDReport/DownloadQtrlyExpectedEntriesITDReport',
    downloadFileBase: 'Livestock_QtrlyExpectedEntriesITD_Report',
    columns: [
      // ReinsuranceYear is a string on this entity (unlike other reports).
      txt('reinsuranceyear', 'Reinsurance Year'),
      num('expectedentriesitd', 'Expected Entries ITD'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'finance-simulated-loss',
    division: 'finance',
    title: 'Finance Simulated Loss Report',
    description:
      'Simulated loss values by measure and account for the selected reinsurance year.',
    requiresYear: true,
    dataMethod: 'POST',
    dataPath: 'SimulatedLossReports/GetFinanceSimulatedLossReport',
    downloadPath: 'SimulatedLossReports/DownloadSimulatedLossReport',
    downloadFileBase: 'Simulated_Loss_Report',
    columns: [
      num('seq', 'Seq'),
      txt('measure', 'Measure'),
      txt('accountnumber', 'Account Number'),
      yr('reinsuranceyear', 'Reinsurance Year'),
      num('value', 'Value'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'reconciliations',
    division: 'finance',
    title: 'Reconciliations Report',
    description:
      'Reconciles investment account balances against the Livestock trial balance — change, per-Livestock-TB balance and difference by account and policy loss position.',
    requiresYear: false,
    dataMethod: 'GET',
    dataPath: 'ReconciliationsReport/GetReconciliationsReport',
    downloadPath: 'ReconciliationsReport/DownloadReconciliationsReport',
    downloadFileBase: 'Livestock_Reconciliations_Report',
    columns: [
      num('seq', 'Seq'),
      txt('investmentaccounts', 'Investment Accounts'),
      txt('accountnum', 'Account Num'),
      txt('policylossposition', 'Policy Loss Position'),
      num('change', 'Change'),
      num('perlivestocktb', 'Per Livestock TB'),
      num('difference', 'Difference'),
      txt('frequency', 'Frequency'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'quarterly-segment',
    division: 'finance',
    title: 'Quarterly Segment Report',
    description: 'Quarterly segment values by measure.',
    requiresYear: false,
    dataMethod: 'GET',
    dataPath: 'QuarterlySegmentReport/GetQuarterlySegmentReport',
    downloadPath: 'QuarterlySegmentReport/DownloadQuarterlySegmentReport',
    downloadFileBase: 'Livestock_QuarterlySegment_Report',
    columns: [
      num('seq', 'Seq'),
      txt('measure', 'Measure'),
      num('value', 'Value'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'ytd-recon',
    division: 'finance',
    title: 'YTD Recon Report',
    description:
      'Year-to-date reconciliation by account: YTD change, per-Livestock-TB balance and the difference between them.',
    requiresYear: false,
    dataMethod: 'GET',
    dataPath: 'YTDReconReport/GetYTDReconReport',
    downloadPath: 'YTDReconReport/DownloadYTDReconReport',
    downloadFileBase: 'Livestock_YTDRecon_Report',
    columns: [
      num('seq', 'Seq'),
      txt('measure', 'Measure'),
      txt('accountnum', 'Account Num'),
      num('ytdchg', 'YTD Chg'),
      num('perlivestocktb', 'Per Livestock TB'),
      num('diff', 'Diff'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'qtd-ytd-segment',
    division: 'finance',
    title: 'QTD/YTD Segment Report',
    description: 'Quarter-to-date and year-to-date segment values by measure.',
    requiresYear: false,
    dataMethod: 'GET',
    dataPath: 'QTDYTDSegmentReport/GetQTDYTDSegmentReport',
    downloadPath: 'QTDYTDSegmentReport/DownloadQTDYTDSegmentReport',
    downloadFileBase: 'Livestock_QTDYTDSegment_Report',
    columns: [
      num('seq', 'Seq'),
      txt('measure', 'Measure'),
      num('qtd', 'QTD'),
      num('ytd', 'YTD'),
      dte('rundate', 'Run Date')
    ]
  },
  {
    key: 'sop',
    division: 'finance',
    title: 'SOP Report',
    description:
      'Policy-level premium, indemnity, reinsurance, commission and allocation detail for leadership, for the selected reinsurance year.',
    requiresYear: true,
    // Server-paged: the API returns a PagedResponse (max 400 rows per page).
    paged: true,
    dataMethod: 'POST',
    dataPath: 'SOPReport/GetLiveStockSOPReport',
    downloadPath: 'SOPReport/DownloadSOPReport',
    downloadFileBase: 'Livestock_SOP_Report',
    columns: [
      yr('reinsuranceyear', 'Reinsurance Year'),
      txt('insuranceplanabbreviation', 'Insurance Plan'),
      txt('funddesignationflag', 'Fund Designation Flag'),
      // Identifiers — rendered as plain text, not formatted numbers.
      txt('growerid', 'Grower ID'),
      txt('policylossposition', 'Policy Loss Position'),
      txt('premiumlineoid', 'Premium Line OID'),
      txt('policynumber', 'Policy Number'),
      num('commission', 'Commission'),
      txt('agencyname', 'Agency Name'),
      dte('effectivedate', 'Effective Date'),
      dte('enddate', 'End Date'),
      txt('qtrstatus', 'Qtr Status'),
      num('gwp', 'GWP'),
      num('nwp', 'NWP'),
      num('grosspaidindemnity', 'Gross Paid Indemnity'),
      num('grosssimindemnity', 'Gross Sim Indemnity'),
      num('grossindemnity', 'Gross Indemnity'),
      num('netindemn', 'Net Indemnity'),
      num('privatereinpremium', 'Private Rein Premium'),
      num('privatereinlosses', 'Private Rein Losses'),
      num('xolpremium', 'XOL Premium'),
      num('adjustedcommission', 'Adjusted Commission'),
      num('afrcost', 'AFR Cost'),
      num('aando', 'A&O'),
      num('allocationsplit', 'Allocation Split'),
      num('netallocationsplit', 'Net Allocation Split'),
      num('grossallocation', 'Gross Allocation'),
      num('netallocation', 'Net Allocation'),
      num('premiumreturn', 'Premium Return'),
      num('indemnityreturn', 'Indemnity Return'),
      dte('qtrrundate', 'Qtr Run Date'),
      txt('reportingquarter', 'Reporting Quarter')
    ]
  }
];

const ALL_REPORTS: ReportDefinition[] = [...ACTUARY_REPORTS, ...FINANCE_REPORTS];

/** Every report across all divisions — used by the header's global search. */
export function getAllReports(): ReportDefinition[] {
  return ALL_REPORTS;
}

export function getReportsByDivision(division: Division): ReportDefinition[] {
  return ALL_REPORTS.filter((r) => r.division === division);
}

export function getReport(division: Division, key: string): ReportDefinition | undefined {
  return ALL_REPORTS.find((r) => r.division === division && r.key === key);
}
